import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { getSignInPath, Routes } from '@/router';
import { FormField, ErrorMode, CustomerProfile } from '@/models';
import { external as externalConstants } from '@/constants';
import {
    makeFormField,
    makeFormFieldOfType,
    isValidCustomerName,
    isValidPostalCode,
    stripNonNumerics,
    enforceMaxLength
  } from '@/helpers';
  import { SweepstakeRequest } from '@/models/leaderboard';

interface SweepstakeForm {
  firstName: FormField;
  lastName: FormField;
  email: FormField;
  telephone: FormField;
  address1: FormField;
  address2: FormField;
  city: FormField;
  state: FormField;
  postalCode: FormField;
  agreeToRules: FormField<boolean | null>;
}

const getFormDefaults = (form: SweepstakeForm): SweepstakeForm => {
  form.firstName = makeFormField();
  form.lastName = makeFormField();
  form.email = makeFormField();
  form.telephone = makeFormField();
  form.address1 = makeFormField();
  form.address2 = makeFormField();
  form.city = makeFormField();
  form.state = makeFormField();
  form.postalCode = makeFormField();
  form.agreeToRules = makeFormFieldOfType<boolean | null>(false);

  return form;
};

const createSweepstakeForm: SweepstakeForm = getFormDefaults({} as SweepstakeForm);

interface FormattedState {
    formatted: string;
    value: string;
}

@Component({
  name: 'CmgSweepstakePage',
})

export default class CmgSweepstakePage extends Vue {
    isLoading = false;
    isFormValid = true;
    sweepstakeForm = createSweepstakeForm;
    formSubmissionErrorMessage = '';
    shouldShowStatePlaceholder = false;

    get form(): SweepstakeForm {
        return this.sweepstakeForm;
    }

    get headingText() {
        return this.isGrandPrizeSweepstake
        ? `SUMMER OF EXTRAS 'FREE BURRITOS FOR A YEAR' SWEEPSTAKES`
        : `SUMMER OF EXTRAS 'FREE ENTREE' SWEEPSTAKES`;
    }

    get isWeeklySweepstake(): boolean {
      return this.$router.currentRoute.name === Routes.SummerSweepstakeWeekly;
    }

    get isGrandPrizeSweepstake(): boolean {
      return this.$router.currentRoute.name === Routes.SummerSweepstakeGrandPrize;
    }

    get isSplitReady(): boolean {
      return !!this.$appStore.state.featureFlags.customerFlagsKey;
    }

    get isAmoePageEnabled(): boolean {
      return this.isWeeklySweepstake
        ? this.$appStore.state.featureFlags.featureFlags.isSummerAmoeWeeklyEnabled
        : this.$appStore.state.featureFlags.featureFlags.isSummerAmoeGrandPrizeEnabled;
    }

    get modalImageSrc(): string {  
        return this.$appStore.state.cms.images.logos.medallionReward.default;
    }

    get customerProfile() {
        return this.$appStore.state.customer.profile;
    }

    get states(): FormattedState[] {
        return externalConstants.states;
    }    

    async mounted() {
      await this.waitForSplitReady();
      if (!this.isAmoePageEnabled) {
        window.location.replace(`${window.location.origin}/404`);
        return;
      }

      const isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
      if (!isLoggedIn) {
        this.$router.push(getSignInPath(this.$route.path));
        return;
      }

      const isEnrolled = this.$appStore.state.customer.loyalty.isEnrolled;
      if (!isEnrolled) {
        this.$services.ui.setModalVisibility('sweepstakeModal', true, {
            title: 'YOU MUST BE A REWARDS MEMBER TO PARTICIPATE IN THE SWEEPSTAKES'
        });  
      }      

      this.initializeForm();
    }

  private waitForSplitReady(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isSplitReady) {
        resolve();
        return;
      }

      const unwatch = this.$appStore.watch(
        (state) => state.featureFlags.customerFlagsKey,
        (newValue) => {
          if (newValue) {
            unwatch();
            resolve();
          }
        }
      );
    });
  }

  initializeForm(): void {
    this.isFormValid = true;
    this.formSubmissionErrorMessage = '';
    if (!this.customerProfile) {
      return;
    }

    this.form.firstName.value = this.customerProfile.firstName;
    this.form.lastName.value = this.customerProfile.lastName;
    this.form.email.value = this.customerProfile.email;
    this.form.telephone.value = this.customerProfile.phoneNumber;
    this.form.postalCode.value = this.customerProfile.zip || '';
  }    

  validateFirstName(): void {
    if (this.form.firstName.value.length == 0) {
      this.form.firstName.error = 'First Name Required';
      this.form.firstName.errorMode = ErrorMode.PlaceHolder;
    } else if (!isValidCustomerName(this.form.firstName.value)) {
      this.form.firstName.error = 'No special characters allowed';
    } else {
      this.form.firstName.error = null;
    }
  }

  validateLastName(): void {
    if (this.form.lastName.value.length == 0) {
      this.form.lastName.error = 'Last Name Required';
    } else if (!isValidCustomerName(this.form.lastName.value)) {
      this.form.lastName.error = 'No special characters allowed';
    } else {
      this.form.lastName.error = null;
    }

    this.form.lastName.errorMode = ErrorMode.PlaceHolder;
  }  

  validateAddress(): void {
    if (this.form.address1.value.length == 0) {
        this.form.address1.error = 'Address1 Required';
    } 
    else {
        this.form.address1.error = null;
    }
  
    this.form.address1.errorMode = ErrorMode.PlaceHolder;
  }

  validateCity(): void {
    if (this.form.city.value.length == 0) {
        this.form.city.error = 'City Required';
    } 
    else {
        this.form.city.error = null;
    }
  
    this.form.city.errorMode = ErrorMode.PlaceHolder;
  }  

  validateState(): void {
    if (this.form.state.value.length == 0) {
        this.formSubmissionErrorMessage = 'State Required';
        this.isFormValid = false;
    } 
    else {
        this.formSubmissionErrorMessage = '';
        this.isFormValid = true;
        this.form.state.error = null;
    }
  
    this.form.state.errorMode = ErrorMode.BelowField;
  }  

  validatePostalCode(): void {
    if (this.form.postalCode.value.length == 0) {
        this.form.postalCode.error = 'Zip Code Required';
    } else if (!isValidPostalCode(this.form.postalCode.value)) {
        this.form.postalCode.error = 'Invalid Zip Code';
    } else {
        this.form.postalCode.error = null;
    }
  
    this.form.postalCode.errorMode = ErrorMode.PlaceHolder;
  }  
  
  enforceMaxLength(value: string, validLengths: number[], formField: FormField): string {
    return enforceMaxLength(value, validLengths, formField);
  }

  stripNonNumerics(value: string): string {
    return stripNonNumerics(value);
  }  

  validateForm() {
    this.formSubmissionErrorMessage = '';
    this.isFormValid = true;

    this.validateFirstName();
    this.validateLastName();
    this.validateAddress();
    this.validateCity();
    this.validateState();
    this.validatePostalCode();

    for (const key of Object.keys(this.form) as Array<keyof SweepstakeForm>) {
      if (this.form[key].error) {
        this.form[key].dirty = true;
        this.isFormValid = false;
      }
    }

    if (!this.form.agreeToRules.value) {
        this.formSubmissionErrorMessage = 'You must agree to the official rules.';
        this.isFormValid = false;
    }
  }

  toggleOfficialRules(): void {
    console.log('rules', this.form.agreeToRules.value);
    this.form.agreeToRules.value = !this.form.agreeToRules.value;
  }

  onSelectState(state: string) {
    this.form.state.value = state;
  }

  async onSubmitSweepstake() {
    const isEnrolled = this.$appStore.state.customer.loyalty.isEnrolled;
    if (!isEnrolled) {
      this.$services.ui.setModalVisibility('sweepstakeModal', true, {
          title: 'YOU MUST BE A REWARDS MEMBER TO PARTICIPATE IN THE SWEEPSTAKES'
      });  
      return;
    } 

    this.isLoading = true;
    this.validateForm();

    if (!this.isFormValid) {
      this.isLoading = false;
      return;
    }  

    const sweepstakeRequest: SweepstakeRequest = {
      sweepstakeTypeId: this.isGrandPrizeSweepstake ? 1 : 2,
      firstName: this.form.firstName.value,
      lastName: this.form.lastName.value,
      email: this.form.email.value,
      phoneNumber: this.form.telephone.value,
      address1: this.form.address1.value,
      address2: this.form.address2.value,
      city: this.form.city.value,
      state: this.form.state.value,
      zipCode: this.form.postalCode.value,
    };    

    let title = 'SWEEPSTAKE ENTRY SUBMITTED!';
    let body = 'Winners will be notified through email. Good luck!';

    try {
        const result = await this.$services.leaderboard.saveSweepstake(sweepstakeRequest);
    }
    catch (error: any) { 
        title = error.response.data.errorMessage;
        body = '';       
    }

    this.isLoading = false;
    this.$services.ui.setModalVisibility('sweepstakeModal', true, {
        title: title,
        body: body
    });    
  }
}